import React from "react";

// The state machine behind SearchBox. Consumed only by SearchBox, which is where
// the memo boundary lives.
//
// Three correctness problems this solves, all of which the naive version had:
//
//   1. A request per keystroke. Typing "pajak" fired five searches, four of them
//      garbage, and whichever resolved last won — so results could disagree with
//      the box.
//   2. The URL was rewritten on every keystroke, so history and any shared link
//      carried half-typed fragments.
//   3. One- and two-character queries. `pg_search` runs with `prefix: true`, so a
//      single letter matches almost everything: noise, from the most expensive
//      query the endpoint can run.
//
// And one performance requirement: typing must cost what typing in a plain HTML
// input costs.
//
// THE INPUT IS UNCONTROLLED. That is the whole design. A controlled input makes
// React re-render and rewrite the DOM value on every keystroke; here the browser
// owns the value (via `inputRef` + `defaultValue`) and React is only told about
// STATUS TRANSITIONS. Typing "akuntansipajak" produces three renders — one per
// visible status change — not fourteen. Keystrokes 4 through 14 are free.
//
// The committed term lives in a ref for the same reason: nothing renders from it.

export const MIN_QUERY_LENGTH = 3;

// 900ms: long enough to swallow a normal typing cadence (~150-250ms between
// keystrokes) without feeling stalled. Enter always skips the wait.
export const SEARCH_DEBOUNCE_MS = 900;

// What the UI should be telling the user right now.
//
//   'empty'     nothing typed — no search constraint is applied
//   'too_short' 1..MIN-1 characters — deliberately not searching yet
//   'waiting'   long enough, debounce running — "finish typing and we'll go"
//   'settled'   the box matches what was committed
export const SEARCH_PHASE = {
  empty: "empty",
  tooShort: "too_short",
  waiting: "waiting",
  settled: "settled",
};

function statusFor(value, minLength, waiting) {
  const length = value.trim().length;
  if (length === 0) {
    return { phase: SEARCH_PHASE.empty, charactersNeeded: minLength };
  }
  if (length < minLength) {
    return {
      phase: SEARCH_PHASE.tooShort,
      charactersNeeded: minLength - length,
    };
  }
  return {
    phase: waiting ? SEARCH_PHASE.waiting : SEARCH_PHASE.settled,
    charactersNeeded: 0,
  };
}

function sameStatus(a, b) {
  return a.phase === b.phase && a.charactersNeeded === b.charactersNeeded;
}

export default function useDebouncedSearch({
  initialValue = "",
  onCommit,
  minLength = MIN_QUERY_LENGTH,
  delay = SEARCH_DEBOUNCE_MS,
}) {
  const inputRef = React.useRef(null);
  const [status, setStatus] = React.useState(() =>
    statusFor(initialValue, minLength, false),
  );

  const timerRef = React.useRef(null);
  // Last value handed to `onCommit`; exists only to suppress duplicate commits.
  const committedRef = React.useRef(initialValue.trim());
  // Refs so every callback below has an empty dependency array and therefore a
  // permanently stable identity — otherwise a parent render would rebuild the
  // handlers and restart the debounce timer mid-typing.
  const onCommitRef = React.useRef(onCommit);
  onCommitRef.current = onCommit;
  const configRef = React.useRef({ minLength, delay });
  configRef.current = { minLength, delay };

  const clearTimer = React.useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Returning the previous object when nothing changed makes React bail out of
  // the re-render entirely. This is what makes keystrokes inside a single phase
  // cost nothing.
  const publishStatus = React.useCallback((next) => {
    setStatus((prev) => (sameStatus(prev, next) ? prev : next));
  }, []);

  // A value below the threshold commits as "" — no search constraint. Holding the
  // previous search instead would leave the list showing results for text that is
  // no longer in the box, which reads as a bug.
  const commit = React.useCallback(
    (raw) => {
      clearTimer();
      const { minLength: min } = configRef.current;
      publishStatus(statusFor(raw, min, false));

      const trimmed = raw.trim();
      const next = trimmed.length >= min ? trimmed : "";
      if (next === committedRef.current) return;

      committedRef.current = next;
      onCommitRef.current(next);
    },
    [clearTimer, publishStatus],
  );

  const onInputChange = React.useCallback(
    (event) => {
      const raw = event.target.value;
      const { minLength: min, delay: wait } = configRef.current;
      clearTimer();

      // Emptying the box is a request to see everything again; making that wait
      // out the full debounce feels broken.
      if (raw.trim().length === 0) {
        commit("");
        return;
      }

      publishStatus(statusFor(raw, min, true));
      timerRef.current = setTimeout(() => commit(raw), wait);
    },
    [clearTimer, commit, publishStatus],
  );

  // Enter skips the wait, Escape clears. Both are what a keyboard user expects,
  // and Enter is the escape hatch for anyone who finds the debounce slow.
  const keyDown = React.useCallback(
    (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        commit(event.target.value);
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        // Uncontrolled input: the value has to be written directly.
        event.target.value = "";
        commit("");
      }
    },
    [commit],
  );

  // Re-seeds the box from outside (a filter reset) WITHOUT firing a commit — the
  // caller is already resetting the query itself.
  const reset = React.useCallback(
    (value = "") => {
      clearTimer();
      if (inputRef.current) inputRef.current.value = value;
      committedRef.current = value.trim();
      publishStatus(statusFor(value, configRef.current.minLength, false));
    },
    [clearTimer, publishStatus],
  );

  React.useEffect(() => clearTimer, [clearTimer]);

  return {
    inputRef,
    status,
    minLength,
    onInputChange,
    keyDown,
    reset,
  };
}
